import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KTIcon } from '../../../_metronic/helpers';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { axiosClientAuth } from '../../utils/axiosClient';
import { useSortBy, useTable } from 'react-table';
import Swal from 'sweetalert2';
import { useIntl } from 'react-intl'
import RejectionReasonModal from '../../../_metronic/partials/widgets/_new/modal/RejectionReasonModal';

type HopStatus = {
  active: boolean | null
}

type HopEntry = {
  id: string
  source?: { id: string | number; name: string; type: string }
  destination?: { id: string | number; name: string; type: string }
  status?: {
    self?: HopStatus
    destination?: HopStatus
    ceind?: HopStatus
    celao?: HopStatus
    indsp?: HopStatus
  }
  created_at?: string
  updated_at?: string
}

const parseJsonPreservingLargeIds = <T,>(raw: string): T =>
  JSON.parse(raw.replace(/(:|\[|,)\s*(\d{16,})(?=\s*[,}\]])/g, '$1"$2"'))

export default function HopsApproval() {
  const intl = useIntl()
  const [data, setData] = useState<HopEntry[]>([]);
  const [hopsPage, setHopsPage] = useState(1)
  const [hopsPageSize, setHopsPageSize] = useState(10)
  const [hopsTotalCount, setHopsTotalCount] = useState(0)
  const [hopsTotalPages, setHopsTotalPages] = useState(1)
  const [searchInput, setSearchInput] = useState('')   // shown in input
  const [searchParam, setSearchParam] = useState('')   // debounced → sent to API
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { showLoader, hideLoader } = useLayout()
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null)
  const [pendingHop, setPendingHop] = useState<HopEntry | null>(null)

  const fetchHopsRequest = useCallback((page = hopsPage, pageSize = hopsPageSize, search = searchParam) => {
    const params: Record<string, any> = { page, page_size: pageSize }
    if (search) params.search = search
    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {})
    ).toString()

    axiosClientAuth
      .get(`/routes/hops/destination/?${queryString}`, {
        transformResponse: [(data) => data],
      })
      .then((resp) => {
        const parsed = parseJsonPreservingLargeIds<any>(resp.data)
        const responseData = parsed?.data || {}
        setData(responseData?.results || [])
        setHopsTotalCount(responseData?.count || 0)
        setHopsTotalPages(Math.max(1, responseData?.total_pages || 1))
      })
      .catch((err) => {
        console.log(err)
        setData([])
        setHopsTotalCount(0)
        setHopsTotalPages(1)
      })
  }, [hopsPage, hopsPageSize, searchParam])

  useEffect(() => {
    fetchHopsRequest()
  }, [fetchHopsRequest])

  const submitHopDecision = useCallback((hopData: HopEntry, action: 'approve' | 'reject', reason = '') => {
    showLoader()
    axiosClientAuth
      .post(`/routes/hops/${hopData?.id}/decision/`, { action, reason })
      .then(() => {
        fetchHopsRequest()
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
          text: action === 'approve'
            ? intl.formatMessage({ id: 'HOPSAPPROVAL.MESSAGES.HOP_APPROVED' })
            : intl.formatMessage({ id: 'HOPSAPPROVAL.MESSAGES.HOP_REJECTED' }),
          icon: 'success',
        })
      })
      .catch((err) => {
        console.log(err)
        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.FAILED' }),
          text: intl.formatMessage({ id: 'HOPSAPPROVAL.MESSAGES.DECISION_FAILED' }),
          icon: 'error',
        })
      })
      .finally(() => {
        hideLoader()
      })
  }, [fetchHopsRequest, showLoader, hideLoader, intl])

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: "srno",
        Cell: ({ ...props }) => {
          return <>{(hopsPage - 1) * hopsPageSize + props.row.index + 1}</>;
        },
      },
      {
        Header: intl.formatMessage({ id: 'HOPSAPPROVAL.TABLE.COL_PARENT_NAME' }),
        accessor: (row: HopEntry) => row?.source?.name,
        Cell: ({ ...props }) => (
          <>
            <span>{props.value || '--'}</span>
            {props.row.original?.source?.id && (
              <span className='d-block text-muted fw-semibold fs-7'>
                {props.row.original.source.id}
              </span>
            )}
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'HOPS.TABLE.COL_HOP_TYPE' }),
        accessor: (row: HopEntry) => row?.source?.type,
        Cell: ({ ...props }) => <>{props.value || '--'}</>,
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.STATUS' }),
        accessor: (row: HopEntry) => row?.status?.self?.active,
        Cell: ({ ...props }) => {
          if (props.value === null || props.value === undefined) {
            return <span className='badge badge-light-warning fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.PENDING' })}</span>
          }
          return <>{props.value ? intl.formatMessage({ id: 'COMMON.STATUS.ENABLED' }) : intl.formatMessage({ id: 'COMMON.STATUS.DISABLED' })}</>;
        },
      },
      {
        Header: intl.formatMessage({ id: 'HOPSAPPROVAL.TABLE.COL_APPROVAL_STATUS' }),
        accessor: (row: HopEntry) => row?.status?.destination?.active,
        Cell: ({ ...props }) => {
          return props.value === true ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.APPROVED' })}</span>
          ) : props.value === false ? (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.REJECTED' })}</span>
          ) : (
            <span className='badge badge-light-warning fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.STATUS.PENDING' })}</span>
          )
        },
      },
      {
        id: 'action',
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        Cell: ({ ...props }) => {
          return (
            <>
              <button
                className='btn h-35px btn-icon btn btn-light-primary me-3'
                onClick={() => {
                  setPendingHop(props.row.original)
                  setPendingAction('approve')
                  setModalOpen(true)
                }}
                disabled={props?.row?.original?.status?.destination?.active === true}
              >
                <KTIcon iconName='check' className='text-primary text-hover-white fs-1' />
              </button>
              <button
                className='btn h-35px btn-icon btn btn-light-danger me-3'
                onClick={() => {
                  setPendingHop(props.row.original)
                  setPendingAction('reject')
                  setModalOpen(true)
                }}
                disabled={
                  props?.row?.original?.status?.destination?.active != null &&
                  !Boolean(props?.row?.original?.status?.destination?.active)
                }
              >
                <KTIcon iconName='cross' className='text-danger text-hover-white fs-1' />
              </button>
            </>
          );
        },
      },
    ],
    [hopsPage, hopsPageSize, submitHopDecision, intl]
  );

  const mTable = useTable<any>(
    {
      columns: columns as any,
      data: data as any,
    },
    useSortBy
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}></PageTitle>
      <div className="row g-5 g-xl-8">
        <div className="col-12">
          <div className={`card`}>
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">
                  {intl.formatMessage({ id: 'HOPSAPPROVAL.TABLE.TITLE' })}
                </span>
                <span className="text-muted fw-semibold fs-7">
                  {intl.formatMessage({ id: 'HOPS.TABLE.TOTAL_RECORDS' }, { count: hopsTotalCount })}
                </span>
              </h3>
              <div className="d-flex align-items-center">
                <div className="d-flex align-items-center position-relative my-1 ms-2">
                  <KTIcon iconName="magnifier" className="text-white-500 position-absolute ms-2" />
                  <input
                    type="text"
                    id="kt_filter_search"
                    className="form-control form-control-solid form-select-sm w-150px ps-9"
                    placeholder={intl.formatMessage({ id: 'HOPS.TABLE.SEARCH_PLACEHOLDER' })}
                    value={searchInput}
                    onChange={(e) => {
                      const val = e.target.value
                      setSearchInput(val)
                      if (debounceTimer.current) clearTimeout(debounceTimer.current)
                      debounceTimer.current = setTimeout(() => {
                        setSearchParam(val)
                        setHopsPage(1)
                      }, 400)
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="card-body pt-3">
              <div className="table-responsive">
                <table
                  {...mTable.getTableProps()}
                  className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer"
                >
                  <thead>
                    {mTable.headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            className={column.id === 'action' ? "w-200px" : ""}
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                          >
                            <span className="text-gray-400 fs-7">
                              {column.render("Header")}
                            </span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <KTIcon iconName="down" className="text-gray ms-1" />
                              ) : (
                                <KTIcon iconName="up" className="text-gray ms-1" />
                              )
                            ) : (
                              ""
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...mTable.getTableBodyProps()}>
                    {(mTable.rows.length > 0 &&
                      mTable.rows.map((row) => {
                        mTable.prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td className="fw-bold" {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })) || (
                        <tr>
                          <td colSpan={7} className="text-center">
                            <span className="fw-bold">
                              {intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}
                            </span>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center mt-5">
                  <div>
                    <select
                      className="form-select form-select-sm form-select-solid"
                      value={hopsPageSize}
                      onChange={(e) => {
                        setHopsPageSize(parseInt(e.target.value, 10))
                        setHopsPage(1)
                      }}
                    >
                      {[5, 10, 20, 25, 50, 100].map((item) => (
                        <option>{item}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span>
                      {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                      <strong>
                        {hopsPage} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {hopsTotalPages}
                      </strong>
                    </span>
                  </div>
                  <div>
                    <button
                      className="btn btn-sm"
                      onClick={() => setHopsPage((prev) => Math.max(1, prev - 1))}
                      disabled={hopsPage <= 1}
                    >
                      <KTIcon iconName="left" className="text-gray fs-1 me-5" />
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => setHopsPage((prev) => Math.min(hopsTotalPages, prev + 1))}
                      disabled={hopsPage >= hopsTotalPages}
                    >
                      <KTIcon iconName="right" className="text-gray fs-1 me-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RejectionReasonModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        onSubmit={(reason) => {
          if (pendingHop && pendingAction) {
            submitHopDecision(pendingHop, pendingAction, reason)
          }
          setModalOpen(false)
        }}
        actionType={pendingAction === 'approve' ? 'whitelist' : 'blacklist'}
        entityType='hop'
      />
    </>
  )
}
